import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authAPI } from '../services/api';
import { Capacitor } from '@capacitor/core';

const OnboardingContext = createContext(null);

// Onboarding steps configuration
const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    title: 'Welcome to Hisab Journey!',
    description: 'Let us show you some handy features to get the most out of your finance tracker.',
    icon: 'sparkles',
  },
  {
    id: 'pull-refresh',
    type: 'spotlight',
    target: '[data-onboarding="pull-refresh"]',
    title: 'Pull to Refresh',
    description: 'Pull down anywhere on the screen to refresh your data and get the latest updates.',
    position: 'bottom',
    icon: 'refresh',
  },
  {
    id: 'add-transaction',
    type: 'spotlight',
    target: '[data-onboarding="add-transaction"]',
    title: 'Quick Add Transaction',
    description: 'Tap this button to quickly add income, expense, or transfer between accounts.',
    position: 'top',
    icon: 'plus',
  },
  {
    id: 'swipe-nav',
    type: 'spotlight',
    target: '[data-onboarding="swipe-nav"]',
    title: 'Swipe to Navigate',
    description: 'Swipe left or right on the bottom navigation to quickly switch between pages.',
    position: 'top',
    icon: 'hand',
  },
  {
    id: 'more-menu',
    type: 'spotlight',
    target: '[data-onboarding="more-menu"]',
    title: 'More Options',
    description: 'Tap More for additional options. Pull down to close the menu quickly.',
    position: 'top',
    icon: 'menu',
  },
  {
    id: 'complete',
    type: 'modal',
    title: "You're All Set!",
    description: 'Explore Categories to reorder them by holding the grip icon. You can replay this tutorial anytime from Settings.',
    icon: 'check',
  },
];

export function OnboardingProvider({ children }) {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [hasCompletedThisSession, setHasCompletedThisSession] = useState(false);

  // Check if should show onboarding (mobile only, first time, dashboard only)
  const shouldShowOnboarding = useCallback(() => {
    if (!user) return false;
    if (user.hasSeenOnboarding) return false;
    if (hasCompletedThisSession) return false;

    // Only show on dashboard (not on set-password, security-setup, etc.)
    if (location.pathname !== '/') return false;

    // Only show on mobile
    const isMobile = Capacitor.isNativePlatform() || window.innerWidth < 1280;
    return isMobile;
  }, [user, hasCompletedThisSession, location.pathname]);

  // Auto-start onboarding for first-time users
  useEffect(() => {
    if (shouldShowOnboarding() && !isActive && !isCompleting) {
      // Small delay to let the dashboard render first
      const timer = setTimeout(() => {
        setIsActive(true);
        setCurrentStepIndex(0);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [shouldShowOnboarding, isActive, isCompleting]);

  // Start onboarding manually (from settings)
  const startOnboarding = useCallback(() => {
    const isMobile = Capacitor.isNativePlatform() || window.innerWidth < 1280;
    if (!isMobile) {
      return; // Don't start on desktop
    }
    setCurrentStepIndex(0);
    setIsActive(true);
  }, []);

  // Go to next step
  const nextStep = useCallback(() => {
    if (currentStepIndex < ONBOARDING_STEPS.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Complete onboarding
      completeOnboarding();
    }
  }, [currentStepIndex]);

  // Go to previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  // Skip onboarding
  const skipOnboarding = useCallback(async () => {
    const skippedAtStep = currentStepIndex;
    setIsActive(false);
    setIsCompleting(true);
    setHasCompletedThisSession(true); // Prevent re-trigger

    try {
      await authAPI.updateProfile({
        hasSeenOnboarding: true,
        onboardingStatus: 'skipped',
        onboardingSkippedAtStep: skippedAtStep
      });
      await refreshUser();
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    } finally {
      setIsCompleting(false);
    }
  }, [refreshUser, currentStepIndex]);

  // Complete onboarding
  const completeOnboarding = useCallback(async () => {
    setIsActive(false);
    setIsCompleting(true);
    setHasCompletedThisSession(true); // Prevent re-trigger

    try {
      await authAPI.updateProfile({
        hasSeenOnboarding: true,
        onboardingStatus: 'completed'
      });
      await refreshUser();
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
    } finally {
      setIsCompleting(false);
    }
  }, [refreshUser]);

  const currentStep = ONBOARDING_STEPS[currentStepIndex];
  const totalSteps = ONBOARDING_STEPS.length;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === totalSteps - 1;
  const isMobile = Capacitor.isNativePlatform() || window.innerWidth < 1280;

  const value = {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isMobile,
    steps: ONBOARDING_STEPS,
    startOnboarding,
    nextStep,
    prevStep,
    skipOnboarding,
    completeOnboarding,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
